import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlogById, authAPI, BLOG_API_BASE_URL } from "../../../hooks/remote/blogApi";
import { extractErrorMessage } from "../../../hooks/constants";
import { toast } from "react-toastify";

const AdminViewBlog = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                const data = await getBlogById(id);
                if (!data || !data.id) {
                    setBlog(null);
                    return;
                }
                const normalizedFeatured = data.featuredImage
                    ? data.featuredImage.startsWith("/")
                        ? data.featuredImage
                        : "/" + data.featuredImage
                    : null;
                setBlog({ ...data, featuredImage: normalizedFeatured });
            } catch (err) {
                const errorMsg = extractErrorMessage(err);
                setError(errorMsg || "Failed to load blog post");
                toast.error(errorMsg || "Failed to load blog post", { theme: "colored" });
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
        window.scrollTo(0, 0);
    }, [id, navigate]);

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
        return `${BLOG_API_BASE_URL.replace("/api", "")}${cleanPath}`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-[#141D26]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 dark:border-primaryBlue mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300 text-lg">Loading blog post...</p>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#141D26] flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8">
                        <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                            Blog Post Not Found
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {error || "This blog post doesn't exist or has been removed."}
                        </p>
                        <button
                            onClick={() => navigate("/blog-admin")}
                            className="bg-blue-600 dark:bg-primaryBlue text-white px-8 py-3 rounded-lg hover:bg-blue-700 dark:hover:bg-primaryBlue/90 transition font-medium"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#141D26]">
            <div className="max-w-4xl mx-auto px-4 py-10">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-8 font-medium text-lg transition group"
                >
                    <svg
                        className="w-6 h-6 mr-2 transform group-hover:-translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    Back
                </button>

                <article className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden">
                    {blog.featuredImage && (
                        <div className="w-full h-96 overflow-hidden">
                            <img
                                src={getImageUrl(blog.featuredImage)}
                                alt={blog.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                        </div>
                    )}

                    <div className="p-8 md:p-12">
                        <div className="mb-8">
                            <h1 className="text-4xl md:text-5xl font-bold mb-3 text-gray-900 dark:text-white leading-tight">
                                {blog.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-3 text-gray-600 dark:text-gray-300 text-sm">
                                {blog.Author && (
                                    <span className="font-medium">By {blog.Author.name}</span>
                                )}
                                {blog.Category && (
                                    <span className="bg-blue-600 dark:bg-primaryBlue text-white px-3 py-1 rounded-full text-xs font-bold">
                                        {blog.Category.name}
                                    </span>
                                )}
                                <span
                                    className={`px-2 py-0.5 rounded text-xs font-semibold ${blog.published
                                            ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400"
                                            : "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400"
                                        }`}
                                >
                                    {blog.published ? "Published" : "Draft"}
                                </span>
                            </div>
                        </div>

                        <hr className="border-t-2 border-gray-200 dark:border-gray-700 mb-8" />

                        <div className="prose prose-lg max-w-none">
                            <div
                                className="text-gray-800 dark:text-gray-200 leading-relaxed wrap-anywhere"
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                            />
                        </div>
                    </div>
                </article>
            </div>
        </div>
    );
};

export default AdminViewBlog;

